import { produce } from 'immer';
import { create } from 'zustand';

import { safeGetTime } from 'src/utils/safe-json';

import {
  savePreferencesToDB,
  loadPreferencesFromDB,
  deletePreferencesFromDB,
  updatePreferenceCategoryInDB,
} from './db-utils';
import {
  Preferences,
  UIPreferences,
  PreferencesSchema,
  QuickPayUsageItem,
  GeneralPreferences,
  PreferenceCategory,
  ReceiptPreferences,
  CheckoutPreferences,
  PreferenceStoreType,
  InventoryPreferences,
  getDefaultPreferences,
} from './types';

export const usePreferenceStore = create<PreferenceStoreType>((set, get) => ({
  // Initial state
  preferences: getDefaultPreferences(),
  isLoaded: false,
  isLoading: false,
  error: null,

  // Load preferences from database
  loadPreferences: async () => {
    set({ isLoading: true, error: null });

    try {
      const preferences = await loadPreferencesFromDB();
      set({
        preferences,
        isLoaded: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load preferences';
      set({
        isLoading: false,
        error: errorMessage,
        preferences: getDefaultPreferences(), // Fallback to defaults
        isLoaded: true,
      });
    }
  },

  // Update UI preferences
  updateUIPreferences: async (newPreferences: Partial<UIPreferences>) => {
    try {
      set(
        produce((state) => {
          state.preferences.ui = { ...state.preferences.ui, ...newPreferences };
          state.preferences.lastUpdated = new Date();
          state.error = null;
        })
      );

      // Save to database
      await updatePreferenceCategoryInDB('ui', newPreferences);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update UI preferences';
      set({ error: errorMessage });

      // Revert changes on error
      await get().loadPreferences();
    }
  },

  // Update checkout preferences
  updateCheckoutPreferences: async (newPreferences: Partial<CheckoutPreferences>) => {
    try {
      set(
        produce((state) => {
          state.preferences.checkout = { ...state.preferences.checkout, ...newPreferences };
          state.preferences.lastUpdated = new Date();
          state.error = null;
        })
      );

      await updatePreferenceCategoryInDB('checkout', newPreferences);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update checkout preferences';
      set({ error: errorMessage });
      await get().loadPreferences();
    }
  },

  // Update inventory preferences
  updateInventoryPreferences: async (newPreferences: Partial<InventoryPreferences>) => {
    try {
      set(
        produce((state) => {
          state.preferences.inventory = { ...state.preferences.inventory, ...newPreferences };
          state.preferences.lastUpdated = new Date();
          state.error = null;
        })
      );

      await updatePreferenceCategoryInDB('inventory', newPreferences);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update inventory preferences';
      set({ error: errorMessage });
      await get().loadPreferences();
    }
  },

  // Update receipt preferences
  updateReceiptPreferences: async (newPreferences: Partial<ReceiptPreferences>) => {
    try {
      set(
        produce((state) => {
          state.preferences.receipts = { ...state.preferences.receipts, ...newPreferences };
          state.preferences.lastUpdated = new Date();
          state.error = null;
        })
      );

      await updatePreferenceCategoryInDB('receipts', newPreferences);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update receipt preferences';
      set({ error: errorMessage });
      await get().loadPreferences();
    }
  },

  // Update general preferences
  updateGeneralPreferences: async (newPreferences: Partial<GeneralPreferences>) => {
    try {
      set(
        produce((state) => {
          state.preferences.general = { ...state.preferences.general, ...newPreferences };
          state.preferences.lastUpdated = new Date();
          state.error = null;
        })
      );

      await updatePreferenceCategoryInDB('general', newPreferences);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update general preferences';
      set({ error: errorMessage });
      await get().loadPreferences();
    }
  },

  // Update entire preferences object
  updatePreferences: async (newPreferences: Partial<Preferences>) => {
    try {
      const updatedPreferences = produce(get().preferences, (draft) => {
        Object.assign(draft, newPreferences);
        draft.lastUpdated = new Date();
      });

      // Validate the updated preferences
      const validated = PreferencesSchema.parse(updatedPreferences);

      set({
        preferences: validated,
        error: null,
      });

      await savePreferencesToDB(validated);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update preferences';
      set({ error: errorMessage });
      await get().loadPreferences();
    }
  },

  // Reset all preferences to defaults
  resetPreferences: async () => {
    try {
      const defaultPreferences = getDefaultPreferences();

      set({
        preferences: defaultPreferences,
        error: null,
      });

      await deletePreferencesFromDB();
      await savePreferencesToDB(defaultPreferences);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset preferences';
      set({ error: errorMessage });
    }
  },

  // Reset specific category to defaults
  resetCategory: async (category: PreferenceCategory) => {
    try {
      const defaultPreferences = getDefaultPreferences();
      const defaultCategoryData = defaultPreferences[category];

      set(
        produce((state) => {
          state.preferences[category] = defaultCategoryData;
          state.preferences.lastUpdated = new Date();
          state.error = null;
        })
      );

      await updatePreferenceCategoryInDB(category, defaultCategoryData);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : `Failed to reset ${category} preferences`;
      set({ error: errorMessage });
      await get().loadPreferences();
    }
  },

  // Getter for specific preference values
  getPreference: <T extends keyof Preferences>(category: T, key: keyof Preferences[T]) => {
    const state = get();
    return state.preferences[category][key];
  },

  // Helper function to get default quick pay amounts
  getDefaultQuickPayAmounts: () => [
    { cents: 500, usageCount: 1, lastUsed: new Date(), firstUsed: new Date() }, // $5
    { cents: 1000, usageCount: 1, lastUsed: new Date(), firstUsed: new Date() }, // $10
    { cents: 2000, usageCount: 1, lastUsed: new Date(), firstUsed: new Date() }, // $20
    { cents: 5000, usageCount: 1, lastUsed: new Date(), firstUsed: new Date() }, // $50
  ],

  // Quick Pay Usage Tracking Methods
  recordQuickPayUsage: async (cents: number) => {
    try {
      const currentState = get();
      const tracking = currentState.preferences.checkout.quickPayUsageTracking;
      const now = new Date();

      // Find existing amount or create new one
      const existingIndex = tracking.amounts.findIndex((item) => item.cents === cents);

      let updatedAmounts: QuickPayUsageItem[];

      if (existingIndex >= 0) {
        // Update existing amount
        updatedAmounts = tracking.amounts.map((item, index) =>
          index === existingIndex
            ? { ...item, usageCount: item.usageCount + 1, lastUsed: now }
            : item
        );
      } else {
        // Add new amount
        const newItem: QuickPayUsageItem = {
          cents,
          usageCount: 1,
          lastUsed: now,
          firstUsed: now,
        };
        updatedAmounts = [...tracking.amounts, newItem];
      }

      // Apply eviction strategy if over limit
      if (updatedAmounts.length > tracking.maxStoredAmounts) {
        // Sort by usage count (ascending) then by last used (ascending)
        // This puts least used and oldest items first for removal
        updatedAmounts.sort((a, b) => {
          if (a.usageCount !== b.usageCount) {
            return a.usageCount - b.usageCount;
          }
          // SAFE: Handle potentially invalid dates
          return safeGetTime(a.lastUsed) - safeGetTime(b.lastUsed);
        });

        // Remove items until we're at the limit
        // Preserve items with usageCount >= 5 unless absolutely necessary
        const itemsToRemove = updatedAmounts.length - tracking.maxStoredAmounts;
        const expendableItems = updatedAmounts.filter((item) => item.usageCount < 5);

        if (expendableItems.length >= itemsToRemove) {
          // Remove only expendable items
          const itemsToRemoveSet = new Set(expendableItems.slice(0, itemsToRemove));
          updatedAmounts = updatedAmounts.filter((item) => !itemsToRemoveSet.has(item));
        } else {
          // Remove expendable items first, then others if needed
          updatedAmounts = updatedAmounts.slice(itemsToRemove);
        }
      }

      // Update preferences
      await get().updateCheckoutPreferences({
        quickPayUsageTracking: {
          ...tracking,
          amounts: updatedAmounts,
        },
      });
    } catch (error) {
      console.error('Failed to record quick pay usage:', error);
    }
  },

  getTopQuickPayAmounts: (count?: number) => {
    const state = get();
    const tracking = state.preferences.checkout.quickPayUsageTracking;
    const displayCount = count || tracking.displayCount;

    // Start with stored amounts
    let availableAmounts = [...tracking.amounts];

    // If we don't have enough amounts, supplement with defaults
    if (availableAmounts.length < displayCount) {
      const defaults = get().getDefaultQuickPayAmounts();
      const existingCents = new Set(availableAmounts.map((item) => item.cents));

      // Add defaults that don't already exist
      const neededDefaults = defaults.filter((item) => !existingCents.has(item.cents));
      availableAmounts = [...availableAmounts, ...neededDefaults];
    }

    // Sort by usage count (descending), then by recency (descending), then by amount (ascending)
    const sortedAmounts = availableAmounts
      .slice() // Create copy to avoid mutating original
      .sort((a, b) => {
        // Primary: usage count (higher first)
        if (a.usageCount !== b.usageCount) {
          return b.usageCount - a.usageCount;
        }
        // Secondary: more recent (higher timestamp first)
        // SAFE: Handle potentially invalid dates
        const aTime = safeGetTime(a.lastUsed);
        const bTime = safeGetTime(b.lastUsed);
        if (aTime !== bTime) {
          return bTime - aTime;
        }
        // Tertiary: lower amount first (for tie-breaking)
        return a.cents - b.cents;
      });

    return sortedAmounts.slice(0, displayCount).map((item) => item.cents);
  },

  clearQuickPayUsage: async () => {
    try {
      const currentState = get();
      const tracking = currentState.preferences.checkout.quickPayUsageTracking;

      await get().updateCheckoutPreferences({
        quickPayUsageTracking: {
          ...tracking,
          amounts: [],
        },
      });
    } catch (error) {
      console.error('Failed to clear quick pay usage:', error);
    }
  },
}));

/**
 * Usage examples:
 *
 * // Basic usage
 * const { preferences, updateUIPreferences } = usePreferenceStore();
 *
 * // Selective subscription
 * const uiPreferences = usePreferenceStore((state) => state.preferences.ui);
 *
 * // Update preferences
 * const updateTheme = usePreferenceStore.getState().updateUIPreferences;
 * await updateTheme({ theme: 'dark' });
 *
 * // Get specific preference
 * const getPreference = usePreferenceStore.getState().getPreference;
 * const currentTheme = getPreference('ui', 'theme');
 */
